import type { ProxyScribeClient as ScribeClient } from "@el8/scribe-client/dist/proxy-client";
import type { IBlobEvent, IMediaRecorder } from "extendable-media-recorder";
import { scribeLogger } from "../logger";

const WAV_HEADER_SIZE = 44;

type WavHeaderInfo = {
  bitsPerSample: number;
  channels: number;
  formatType: number;
  sampleRate: number;
};

export function convertStereoPCMToMono(buffer: ArrayBuffer): ArrayBuffer {
  const stereoView = new DataView(buffer);
  const monoBuffer = new ArrayBuffer(buffer.byteLength / 2);
  const monoView = new DataView(monoBuffer);
  for (let i = 0, j = 0; i < buffer.byteLength; i += 4, j += 2) {
    const leftSample = stereoView.getInt16(i, true);
    const rightSample = stereoView.getInt16(i + 2, true);
    const monoSample = (leftSample + rightSample) / 2;
    monoView.setInt16(j, monoSample, true);
  }
  return monoBuffer;
}

const logWavHeaderInfo = (arr: Uint8Array): void => {
  const view = new DataView(arr.buffer);

  const formatType = view.getUint16(20, true);
  const channels = view.getUint16(22, true);
  const sampleRate = view.getUint32(24, true);
  const bitsPerSample = view.getUint16(34, true);

  const headerInfo: WavHeaderInfo = {
    formatType,
    channels,
    sampleRate,
    bitsPerSample,
  };

  scribeLogger?.info("Received WAV header info", { headerInfo });
};

/**
 * Normalizes an audio chunk by ensuring it either:
 * - is smaller than or equal to the specified target chunk size (in bytes), or
 * - gets split into 2 or more chunks of the target size (the last chunk may
 * be smaller than the target size if the input chunk size is not a multiple
 * of the target size).
 */
export function normalizeAudioChunk(
  buffer: ArrayBuffer,
  targetChunkSize: number,
): ArrayBuffer[] {
  if (targetChunkSize <= 0) {
    throw new Error(
      `Target chunk size must be greater than 0, got: ${targetChunkSize}`,
    );
  }

  if (buffer.byteLength <= targetChunkSize) {
    return [buffer];
  }

  const buffers = [];
  let i = 0;
  while (i < buffer.byteLength) {
    buffers.push(buffer.slice(i, i + targetChunkSize));
    i += targetChunkSize;
  }

  return buffers;
}

/**
 * Strips the WAV header from an audio chunk represented by an array buffer.
 * For more information on the contents of the WAV header, see: https://docs.fileformat.com/audio/wav/
 */
export function stripWavHeaderIfPresent(buffer: ArrayBuffer): ArrayBuffer {
  if (buffer.byteLength > WAV_HEADER_SIZE) {
    // We create this Uint8Array as an intermediate so we can inspect the buffer
    const uint8array = new Uint8Array(buffer);

    // An Resource Interchange File Format (RIFF) header is 12 bytes.
    // If an RIFF header is present, the first 4 bytes will contain the characters "RIFF",
    // and the last 4 bytes will contain the characters "WAVE" for WAV files
    const riff = String.fromCharCode(...uint8array.slice(0, 4));
    const wave = String.fromCharCode(...uint8array.slice(8, 12));

    if (riff === "RIFF" && wave === "WAVE") {
      try {
        logWavHeaderInfo(uint8array);
      } catch {
        // swallow. this logging is just for convenience
      }

      // Remove the full WAV header and convert back to an ArrayBuffer
      return uint8array.slice(WAV_HEADER_SIZE).buffer;
    }
  }

  return buffer;
}

/**
 * Process the data from a MediaRecorder `dataavailable` event into an array of
 * array buffers representing chunks of audio data for sending to scribe client(s).
 *
 * Under the hood, this handles:
 * - Stripping the WAV header if present
 * - Converting stereo PCM data to mono
 * - Normalizing the audio chunk size to match the expected chunk size for the
 * given scribe client
 */
export async function processScribeRecorderData(
  recorder: IMediaRecorder,
  scribeClient: ScribeClient,
  event: IBlobEvent,
): Promise<ArrayBuffer[]> {
  let buffers: ArrayBuffer[];

  let buffer = await event.data.arrayBuffer();
  buffer = stripWavHeaderIfPresent(buffer);
  buffer = convertStereoPCMToMono(buffer);
  buffers = [buffer];

  const isFinalChunk = recorder.state === "inactive";
  if (isFinalChunk) {
    const chunkSize = await scribeClient.getChunkSize();
    if (buffer.byteLength !== chunkSize) {
      buffers = normalizeAudioChunk(buffer, chunkSize);
    }
  }

  return buffers;
}
